import { defaultCountryCode } from '@components/country-code/constants';
import { appendTimeToISODate, formatISODate, isSameDay } from '@utils/date';
import {
  AttendeeDetailsResponse,
  CreateEventRequest,
  EventDetailsResponse,
  EventFormDetails,
  FormField,
  SubTicketRequest
} from 'types/event';

import {
  DEFAULT_SUB_TICKET_TAG,
  DefaultQuestionNames,
  defaultQuestionNames,
  defaultQuestions,
  maxFileSize
} from './constants';
import {
  AttendeeDetails,
  Coupon,
  EventData,
  EventDetails,
  EventSubTicketType,
  Question,
  QuestionMetaData,
  QuestionType,
  Ticket
} from './types';

export function getInitialQuestionData(
  questionType: QuestionType,
  nextDisplayOrder: number
): Question {
  const getMetaData = () => {
    switch (questionType) {
      case QuestionType.NUMBER:
      case QuestionType.DATE:
      case QuestionType.TEXT:
      case QuestionType.TEXT_AREA:
      case QuestionType.MOBILE_NUMBER:
        return null;
      case QuestionType.MULTIPLE_CHOICE:
      case QuestionType.SINGLE_CHOICE:
      case QuestionType.DROP_DOWN:
      case QuestionType.FILE_UPLOAD:
        return ['', ''];
      default:
        return null;
    }
  };
  return {
    id: new Date().getTime().toString(),
    question: '',
    required: false,
    type: questionType,
    questionMetaData: getMetaData(),
    displayOrder: nextDisplayOrder,
    ...(questionType === QuestionType.FILE_UPLOAD && {
      size: maxFileSize
    })
  };
}

const hasDuplicates = (arr: string[]): boolean =>
  arr.some((str, index) => arr.indexOf(str) !== index);

// TODO: Modify below function proper validation
export const isQuestionValid = (
  allQuestionNames: string[],
  question: Question
) => {
  let metadata;

  if (question.question.length === 0) {
    return false;
  }

  if (allQuestionNames.includes(question.question.toLowerCase())) return false;

  switch (question.type) {
    case QuestionType.NUMBER:
    case QuestionType.DATE:
    case QuestionType.TEXT:
    case QuestionType.TEXT_AREA:
    case QuestionType.MOBILE_NUMBER:
      return true;
    case QuestionType.MULTIPLE_CHOICE:
    case QuestionType.SINGLE_CHOICE:
    case QuestionType.DROP_DOWN:
      metadata =
        // eslint-disable-next-line max-len
        question.questionMetaData as QuestionMetaData[QuestionType.MULTIPLE_CHOICE];
      return (
        metadata.length > 1 &&
        metadata.every((option) => option.length > 0) &&
        !hasDuplicates(metadata)
      );
    case QuestionType.FILE_UPLOAD:
      metadata =
        question.questionMetaData as QuestionMetaData[QuestionType.FILE_UPLOAD];
      return (
        metadata.length > 0 && metadata.some((option) => option.length > 0)
      );
    default:
      return false;
  }
};

export const convertToCreateEventRequest = ({
  eventData,
  isEdit,
  originalEventData
}: {
  eventData: EventData;
  isEdit: boolean;
  originalEventData?: EventData;
}): CreateEventRequest => {
  const {
    basicDetails,
    otherDetails,
    questions,
    tickets,
    coupons,
    registrationCloseDate,
    registrationCloseTime,
    isTicketEmailEnabled
  } = eventData;
  const startDate = appendTimeToISODate(
    basicDetails.startDate,
    basicDetails.startTime
  );

  const endDate = appendTimeToISODate(
    basicDetails.endDate,
    basicDetails.endTime
  );

  const registrationCloseDateTime = appendTimeToISODate(
    registrationCloseDate,
    registrationCloseTime
  );

  const getSubTickets = (
    ticket: Ticket,
    originalTicket?: Ticket
  ): SubTicketRequest[] => {
    const eventSubTickets = eventData.subTickets;
    if (isEdit) {
      const defaultSubTicketObject = eventSubTickets.filter(
        (st) => st.name === DEFAULT_SUB_TICKET_TAG.name
      );

      const defaultSubTicket =
        ticket.isSubTicketEnabled || defaultSubTicketObject.length > 0
          ? [
              {
                id:
                  defaultSubTicketObject.length > 0
                    ? defaultSubTicketObject[0].id
                    : null,
                name: DEFAULT_SUB_TICKET_TAG.name,
                to_be_deleted: !ticket.isSubTicketEnabled
              }
            ]
          : ([] as SubTicketRequest[]);

      const addedSubTickets = ticket.subTicket
        ?.map((subTicket) => {
          const subTicketData = eventSubTickets.find(
            (st) => st.id === subTicket
          );
          if (subTicketData?.name === DEFAULT_SUB_TICKET_TAG.name) {
            return null;
          }
          if (subTicketData) {
            return {
              name: subTicketData.name,
              id: subTicketData.id.toString().startsWith('custom-')
                ? null
                : subTicketData.id,
              to_be_deleted: !ticket.isSubTicketEnabled
            };
          }
          return null;
        })
        .filter((st) => st !== null);

      const removedSubTickets = originalTicket?.subTicket
        ?.filter((st) => !ticket.subTicket?.includes(st))
        .map((subTicket) => {
          const subTicketData = eventSubTickets.find(
            (st) => st.id === subTicket
          );
          if (subTicketData?.name === DEFAULT_SUB_TICKET_TAG.name) {
            return null;
          }
          if (subTicketData) {
            return {
              name: subTicketData.name,
              id: subTicketData.id.toString().startsWith('custom-')
                ? null
                : subTicketData.id,
              to_be_deleted: true
            };
          }
          return null;
        })
        .filter((st) => st !== null);

      return [
        ...(defaultSubTicket || []),
        ...(addedSubTickets || []),
        ...(removedSubTickets || [])
      ] as SubTicketRequest[];
    }

    const addedSubTickets = ticket.subTicket
      ?.map((subTicket) => {
        // find the subTicket in eventSubTickets
        const subTicketData = eventSubTickets.find((st) => st.id === subTicket);
        if (subTicketData) {
          return {
            name: subTicketData.name
          };
        }
        return null;
      })
      .filter((st) => st !== null);

    return [
      ...(ticket.isSubTicketEnabled
        ? [{ name: DEFAULT_SUB_TICKET_TAG.name }]
        : []),
      ...(addedSubTickets || [])
    ] as SubTicketRequest[];
  };

  const couponValidTo = (coupon: Coupon) => {
    if (!coupon.valid_to) return null;
    const convertedDate = new Date(coupon.valid_to);
    convertedDate.setHours(23, 59, 59, 999);
    return convertedDate.toISOString();
  };

  return {
    name: basicDetails.title,
    description: basicDetails.description,
    image_url: basicDetails.imageUrl,
    show_business_logo: basicDetails.show_business_logo ?? false,
    is_attendee_details_required_per_ticket:
      basicDetails.allow_multiple_ticket_selection
        ? basicDetails.is_attendee_details_required_per_ticket ?? false
        : false,
    max_tickets_per_booking: basicDetails.allow_multiple_ticket_selection
      ? basicDetails.max_tickets_per_transaction ?? undefined
      : 1,
    venue_name: otherDetails.venueName,
    venue_address_line_1: otherDetails.addressLine1,
    venue_address_line_2: otherDetails.addressLine2,
    venue_city: otherDetails.city,
    venue_state: otherDetails.state,
    venue_pincode: otherDetails.pincode,
    location_link: otherDetails.locationLink,
    contact_name: otherDetails.name,
    contact_email: otherDetails.email,
    contact_phone_country_code: defaultCountryCode,
    contact_phone: otherDetails.primaryNumber,
    contact_alternate_phone: otherDetails.alternateNumber,
    contact_phone_alternate_country_code: otherDetails.alternateNumber
      ? defaultCountryCode
      : '',
    start_date_time: startDate,
    end_date_time: endDate,
    additional_doc_url: otherDetails.additionalDocUrl,
    registration_end_date_time: registrationCloseDateTime,
    event_tickets: tickets.map((ticket) => {
      // Find the original ticket data for comparison
      const originalTicket = originalEventData?.tickets?.find(
        (origTicket) => origTicket.id === ticket.id
      );
      const subTickets = getSubTickets(ticket, originalTicket);
      return {
        id: ticket.id ? Number(ticket.id) : undefined,
        name: ticket.name,
        is_active: ticket.isActive ?? true,
        price: ticket.price,
        description: ticket.description,
        capacity: ticket.capacity,
        sub_tickets: subTickets
      };
    }),
    is_ticket_email_enabled: isTicketEmailEnabled,
    form_fields: questions.map((question) => ({
      id: question.id && isEdit ? Number(question.id) : undefined,
      name: question.question,
      type: question.type,
      is_required: question.required,
      options: (question.questionMetaData ?? []) as string[],
      to_be_deleted: question.isDeleted,
      display_order: question.displayOrder,
      size: question.size
    })),
    discount_coupons: coupons.map((coupon) => ({
      id: coupon.id?.startsWith('new-coupon-') ? '' : coupon.id,
      name: coupon.name,
      code: coupon.code,
      discount_percentage: coupon.discount_percentage,
      discount_amount: coupon.discount_amount,
      valid_from: coupon.valid_from,
      valid_to: couponValidTo(coupon),
      show_during_registration: coupon.show_during_registration,
      discount_amount_max: coupon.discount_amount_max,
      total_count: coupon.total_count,
      discount_eligible_amount_min: coupon.discount_eligible_amount_min,
      is_active: coupon.is_active
    }))
  };
};

const orderFormFields = (formFields: FormField[]) =>
  [...formFields].sort((a, b) => a.display_order - b.display_order);

const getSubTicketIds = (
  subTickets: SubTicketRequest[],
  eventSubTickets: EventSubTicketType[]
) =>
  subTickets?.length > 0
    ? subTickets.map((subTicket) => {
        const eventSubTicket = eventSubTickets.find(
          (st) => st.name === subTicket.name
        );
        return eventSubTicket?.id ?? '';
      })
    : [];

export const convertEventFormDetailsToEventData = (
  eventDetails: EventFormDetails
): EventData => ({
  basicDetails: {
    title: eventDetails.name,
    imageUrl: eventDetails.image_url,
    description: eventDetails.description,
    startDate: eventDetails.start_date_time,
    startTime: formatISODate({
      date: eventDetails.start_date_time,
      format: 'hh:mm am/pm'
    }).toUpperCase(),
    endDate: eventDetails.end_date_time,
    endTime: formatISODate({
      date: eventDetails.end_date_time,
      format: 'hh:mm am/pm'
    }).toUpperCase(),
    show_business_logo: eventDetails.show_business_logo,
    is_attendee_details_required_per_ticket:
      eventDetails.is_attendee_details_required_per_ticket ?? false,
    allow_multiple_ticket_selection:
      eventDetails.max_tickets_per_booking == null ||
      eventDetails.max_tickets_per_booking > 1,
    max_tickets_per_transaction:
      eventDetails.max_tickets_per_booking != null &&
      eventDetails.max_tickets_per_booking > 1
        ? eventDetails.max_tickets_per_booking
        : undefined
  },
  otherDetails: {
    venueName: eventDetails.venue_name,
    addressLine1: eventDetails.venue_address_line1,
    addressLine2: eventDetails.venue_address_line2,
    state: eventDetails.venue_state,
    additionalDocUrl: eventDetails.additional_doc_url,
    city: eventDetails.venue_city,
    pincode: eventDetails.venue_pincode,
    locationLink: eventDetails.location_link,
    name: eventDetails.contact_name,
    email: eventDetails.contact_email,
    primaryNumber: eventDetails.contact_phone,
    alternateNumber: eventDetails.contact_alternate_phone
  },
  questions: orderFormFields(eventDetails.form_fields).map((field) => ({
    id: field.form_id.toString(),
    question: field.field_name,
    type: field.field_type as QuestionType,
    required: field.is_required,
    isDefault:
      field.field_name === DefaultQuestionNames.EMAIL_ID
        ? field.field_type === QuestionType.EMAIL_ID
        : defaultQuestionNames.includes(field.field_name),
    questionMetaData: field.options as QuestionMetaData[QuestionType],
    displayOrder: field.display_order
  })),
  registrationCloseDate: eventDetails.registration_end_date_time,
  registrationCloseTime: formatISODate({
    date: eventDetails.registration_end_date_time,
    format: 'hh:mm am/pm'
  }).toUpperCase(),
  tickets:
    eventDetails.event_tickets?.map((ticket) => ({
      id: ticket.ticket_id.toString(),
      name: ticket.name,
      isActive: ticket.is_active,
      price: ticket.price,
      description: ticket.description,
      capacity: ticket.capacity,
      available_capacity: ticket.available_capacity,
      seats_booked: ticket.seats_booked,
      isSubTicketEnabled: ticket.sub_tickets?.length > 0,
      subTicket: getSubTicketIds(ticket.sub_tickets, eventDetails.sub_tickets)
    })) || [],
  isTicketEmailEnabled: eventDetails.is_ticket_email_enabled,
  coupons: [],
  subTickets: eventDetails.sub_tickets ?? []
});

export const convertFromEventDetailsResponse = (
  response: EventDetailsResponse
): EventDetails => ({
  eventId: response.event_id,
  name: response.name,
  imageUrl: response.image_url,
  venueName: response.venue_name,
  venueCity: response.venue_city,
  registrationEndDateTime: response.registration_end_date_time,
  startDateTime: response.start_date_time,
  endDateTime: response.end_date_time,
  formLink: response.form_link,
  totalRegistrations: response.total_registrations,
  totalTicketSold: response.total_ticket_sold,
  totalRevenue: response.total_revenue,
  tickets: response.tickets.map((ticket) => ({
    name: ticket.name,
    price: ticket.price,
    ticketId: ticket.ticket_id,
    isActive: ticket.is_active,
    seatsBooked: ticket.seats_booked,
    capacity: ticket.capacity,
    subTicketCount: ticket.sub_ticket_count,
    hasSubTicket: ticket.has_sub_ticket,
    subTickets: ticket.sub_tickets?.map((subTicket) => ({
      name: subTicket.name,
      redeemedCount: subTicket.redeemed_count,
      redeemedPercentage: subTicket.redeemed_percentage
    }))
  })),
  status: response.status,
  discountInsights: response.discount_insights
    ? {
        appliedCouponCount: response.discount_insights.applied_coupon_count,
        discountRedeemed: response.discount_insights.discount_redeemed,
        percentageRegistrationsWithDiscount:
          response.discount_insights.percentage_registrations_with_discount,
        activeCouponCount: response.discount_insights.active_coupon_count,
        totalCouponCount: response.discount_insights.total_coupon_count
      }
    : undefined
});

export const convertFromAttendeeDetailsResponse = (
  response: AttendeeDetailsResponse
): AttendeeDetails => ({
  questions: [
    ...response.event_form_submission.map((submission) => ({
      id: submission.id?.toString(),
      question: submission.field_name,
      answer:
        submission.field_type === QuestionType.MULTIPLE_CHOICE
          ? submission.field_value
          : submission.field_value[0],
      type: submission.field_type as QuestionType,
      required: submission.is_required,
      displayOrder: submission.display_order
    }))
  ]
});
const isQuestionMetadataChanged = (
  question: Question,
  prevQuestion: Question
): boolean => {
  switch (question.type) {
    case QuestionType.TEXT:
    case QuestionType.TEXT_AREA:
    case QuestionType.NUMBER:
    case QuestionType.DATE:
    case QuestionType.MOBILE_NUMBER:
      return false;
    case QuestionType.DROP_DOWN:
    case QuestionType.MULTIPLE_CHOICE:
    case QuestionType.SINGLE_CHOICE: {
      const options = question.questionMetaData as string[];
      const prevOptions = prevQuestion.questionMetaData as string[];
      if (options.length !== prevOptions.length) {
        return true;
      }
      return !options.every((option) =>
        prevOptions.some((prevOption) => option === prevOption)
      );
    }
    case QuestionType.FILE_UPLOAD: {
      const options = question.questionMetaData as string[];
      const prevOptions = prevQuestion.questionMetaData as string[];
      return options.some((option) => !prevOptions.includes(option));
    }
    default:
      return false;
  }
};

export const getUpdatedQuestions = (
  prevQuestions: Question[],
  currentQuestions: Question[]
) => {
  const deletedQuestions = prevQuestions
    .filter((question) => !currentQuestions.some((q) => q.id === question.id))
    .map((question) => ({ ...question, isDeleted: true }));

  const updatedQuestions: Question[] = [];
  currentQuestions.forEach((question) => {
    const previouslySavedQuestion = prevQuestions.find(
      (q) => q.id === question.id
    );
    if (previouslySavedQuestion) {
      const isRequiredChanged =
        question.required !== previouslySavedQuestion.required;
      const isMetadataChanged = isQuestionMetadataChanged(
        question,
        previouslySavedQuestion
      );

      const isOrderChanged =
        question.displayOrder !== previouslySavedQuestion.displayOrder;
      if (
        previouslySavedQuestion.question !== question.question ||
        isMetadataChanged
      ) {
        updatedQuestions.push({
          ...question,
          id: '',
          isDeleted: false
        });
        updatedQuestions.push({
          ...previouslySavedQuestion,
          isDeleted: true
        });
      } else if (isOrderChanged || isRequiredChanged) {
        updatedQuestions.push({
          ...question
        });
      }
    } else {
      updatedQuestions.push({
        ...question,
        id: ''
      });
    }
  });
  return [...updatedQuestions, ...deletedQuestions];
};

export const getDateRangeStrings = (startDate: string, endDate: string) => {
  const isSame = isSameDay(startDate, endDate);
  const getFormattedDate = (date: string) =>
    formatISODate({
      date,
      format: 'WK MMM DD-YYYY'
    });
  const getFormattedTime = (date: string) =>
    formatISODate({ date, format: 'hh:mm am/pm' })
      .toUpperCase()
      .replace(' ', '');

  if (isSame) {
    return {
      startDate: `${getFormattedDate(startDate)}, ${getFormattedTime(
        startDate
      )} - ${getFormattedTime(endDate)}`,
      endDate: null
    };
  }
  return {
    startDate: `${getFormattedDate(startDate)}, ${getFormattedTime(startDate)}`,
    endDate: `${getFormattedDate(endDate)}, ${getFormattedTime(endDate)}`
  };
};

export const getRegistrationCloseTime = (
  registrationCloseTime: string,
  registrationCloseDate: string,
  endDate: string,
  endTime: string
) => {
  if (registrationCloseTime) {
    return registrationCloseTime;
  }

  const formattedEventEndDate = formatISODate({
    date: endDate,
    format: 'dd-mm-yyyy'
  });
  const formattedRegistrationEndDate = formatISODate({
    date: registrationCloseDate,
    format: 'dd-mm-yyyy'
  });

  if (formattedEventEndDate === formattedRegistrationEndDate) {
    return endTime;
  }
  const endOfDay = new Date(registrationCloseDate);
  endOfDay.setHours(23, 59, 59, 999);
  endOfDay.toISOString();
  return formatISODate({
    date: endOfDay,
    format: 'hh:mm am/pm'
  }).toUpperCase();
};

export const updateReorderedQuestions = (
  isTicketEmailEnabled: boolean,
  data: Question[]
) => {
  const defaultQuestionIds = defaultQuestions.map((q) => q.question);
  const otherQuestions = data.filter(
    (q) => !defaultQuestionIds.includes(q.question)
  );

  if (isTicketEmailEnabled) {
    const reorderedQuestions = [
      ...defaultQuestions,
      ...otherQuestions.map((q, index) => ({
        ...q,
        displayOrder: index + (defaultQuestions.length + 1)
      }))
    ];

    return reorderedQuestions;
  }

  const reorderedQuestions = [
    ...defaultQuestions.filter((q) => q.type !== QuestionType.EMAIL_ID),
    ...otherQuestions.map((q, index) => ({
      ...q,
      displayOrder: index + defaultQuestions.length
    }))
  ];

  return reorderedQuestions;
};
